/**
 * Calculator state for /learn (#777), evaluated with the order ticket's own functions: centsToPx + economics
 * (ticket/order.ts) and validatePrice / validateSize / minOrderValueError (trading/rules.ts). No formulas here.
 */
import type { Side } from '../hl/outcomes';
import { centsToPx, economics } from '../ticket/order';
import { cmp, decToString, ONE, parseDec, sub, ZERO } from '../trading/decimal';
import { outcomeFee } from '../trading/fees';
import { t } from '../i18n/i18n';
import { minOrderValueError, notionalExact, OUTCOME_RULES, toSidePrice, validatePrice, validateSize } from '../trading/rules';

export interface CalcState {
  side: Side;
  /** Price in cents as typed. */
  cents: string;
  /** Shares as typed. */
  shares: string;
}

export interface CalcResult {
  px: string | null;
  errors: string[];
  econ: ReturnType<typeof economics>;
}

/** Validate and price a calculator state with the ticket's rules (shared by the calculator, timeline and fees). */
export function evaluateCalc(s: CalcState): CalcResult {
  const px = centsToPx(s.cents);
  const errors: string[] = [];
  if (px === null) errors.push(t('learn.calc.enter_price'));
  else {
    const v = validatePrice(px, OUTCOME_RULES);
    if (!v.ok) errors.push(v.reason);
  }
  const sv = validateSize(s.shares.trim(), OUTCOME_RULES);
  if (!sv.ok) errors.push(sv.reason);
  if (errors.length === 0 && px !== null) {
    const m = minOrderValueError(px, s.shares.trim());
    if (m) errors.push(m);
  }
  return { px, errors, econ: errors.length === 0 ? economics(px, s.shares.trim(), true) : null };
}


export interface SettlementResult {
  /** USDC the position converts into: shares * (settleFraction for Yes, 1 - settleFraction for No). */
  value: string;
  /** Hyperliquid settlement fee on that value (fees.ts). */
  fee: string;
  /** value - fee. */
  net: string;
  /** net - cost (cost = what the shares were bought for), or null without a cost. */
  pnl: string | null;
}

/**
 * What a held position turns into at settlement (#777): Yes converts to `settleFraction` USDC per share and No to
 * `1 - settleFraction` (Hyperliquid HIP-4 docs). Prices via rules.toSidePrice / notionalExact, fee via fees.outcomeFee.
 */
export function settlementResult(
  side: Side,
  shares: string,
  settleFraction: string,
  cost: string | null,
  deployerFeeScale?: string | null,
): SettlementResult | null {
  if (!validateSize(shares.trim(), OUTCOME_RULES).ok) return null;
  const f = parseDec(settleFraction);
  if (!f || cmp(f, ZERO) < 0 || cmp(f, ONE) > 0) return null;
  const value = notionalExact(toSidePrice(settleFraction, side), shares.trim());
  const fee = outcomeFee({ event: 'settle', value, deployerFeeScale }).hlFee;
  const net = decToString(sub(parseDec(value)!, parseDec(fee)!));
  const c = cost === null ? null : parseDec(cost);
  return { value, fee, net, pnl: c ? decToString(sub(parseDec(net)!, c)) : null };
}
